import {
  Cartesian3,
  Matrix3,
  Matrix4,
  Quaternion,
  Math as CesiumMath,
} from "cesium";

const v1NormalizedScratch = new Cartesian3();
const v2NormalizedScratch = new Cartesian3();
const crossScratch = new Cartesian3();
const normalizedAxisScratch = new Cartesian3();
const newOffsetScratch = new Cartesian3();
const modelOriginScratch = new Cartesian3();
const axisEndWorldScratch = new Cartesian3();
const axisVectorWorldScratch = new Cartesian3();

export class RotationUtils {
  /**
   * Computes a quaternion representing the rotation from vector v1 to vector v2
   * constrained to rotate around the specified axis. This is used for constrained
   * rotation interactions where the user selects a specific axis (X, Y, or Z) to rotate around.
   *
   * The vectors must already be perpendicular to the rotation axis (i.e., lie in the plane
   * perpendicular to the axis). This is typically achieved by projecting mouse positions
   * onto the rotation plane before creating the vectors.
   *
   * @param v1 - The starting vector (must be perpendicular to rotation axis)
   * @param v2 - The ending vector (must be perpendicular to rotation axis)
   * @param rotationAxis - The axis to constrain the rotation around
   * @param result - The quaternion to store the result
   * @returns The rotation quaternion
   * @throws {Error} If the rotation axis has zero length
   * @throws {Error} If vectors are not perpendicular to the rotation axis
   */
  static quaternionFromVectors(
    v1: Cartesian3,
    v2: Cartesian3,
    rotationAxis: Cartesian3,
    result: Quaternion,
  ): Quaternion {
    const v1Normalized = Cartesian3.normalize(v1, v1NormalizedScratch);
    const v2Normalized = Cartesian3.normalize(v2, v2NormalizedScratch);

    const axisMagnitude = Cartesian3.magnitude(rotationAxis);
    if (axisMagnitude < CesiumMath.EPSILON7) {
      throw new Error("Rotation axis must have non-zero length");
    }

    const normalizedAxis = Cartesian3.normalize(
      rotationAxis,
      normalizedAxisScratch,
    );

    // Validate that vectors are perpendicular to the rotation axis
    const v1DotAxis = Math.abs(Cartesian3.dot(v1Normalized, normalizedAxis));
    const v2DotAxis = Math.abs(Cartesian3.dot(v2Normalized, normalizedAxis));

    if (v1DotAxis > CesiumMath.EPSILON5) {
      throw new Error(
        `Vector v1 is not perpendicular to rotation axis (dot product: ${v1DotAxis}). ` +
          `Vectors must lie in the plane perpendicular to the rotation axis.`,
      );
    }

    if (v2DotAxis > CesiumMath.EPSILON5) {
      throw new Error(
        `Vector v2 is not perpendicular to rotation axis (dot product: ${v2DotAxis}). ` +
          `Vectors must lie in the plane perpendicular to the rotation axis.`,
      );
    }

    const cross = Cartesian3.cross(v1Normalized, v2Normalized, crossScratch);
    const dot = Cartesian3.dot(v1Normalized, v2Normalized);

    const crossDotAxis = Cartesian3.dot(cross, normalizedAxis);
    const angle = Math.atan2(Cartesian3.magnitude(cross), dot);
    const signedAngle = crossDotAxis >= 0 ? angle : -angle;

    const halfAngle = signedAngle / 2;
    const sinHalfAngle = Math.sin(halfAngle);

    result.x = normalizedAxis.x * sinHalfAngle;
    result.y = normalizedAxis.y * sinHalfAngle;
    result.z = normalizedAxis.z * sinHalfAngle;
    result.w = Math.cos(halfAngle);

    return result;
  }

  /**
   * Calculates the new model origin after rotation around a pivot point (widget origin)
   * This is needed because rotation happens around the widget, not the model center
   * @param widgetOrigin - The point to rotate around
   * @param offsetFromModelToWidget - The offset vector from model origin to widget origin (in world space)
   * @param rotation - The rotation matrix to apply
   * @param result - The Cartesian3 to store the result
   * @returns The new model origin position
   */
  static calculateRotatedModelOrigin(
    widgetOrigin: Cartesian3,
    offsetFromModelToWidget: Cartesian3,
    rotation: Matrix3,
    result: Cartesian3,
  ): Cartesian3 {
    // Rotate the offset vector
    let newOffset = Matrix3.multiplyByVector(
      rotation,
      offsetFromModelToWidget,
      newOffsetScratch,
    );
    // New model origin = widget origin - rotated offset
    return Cartesian3.subtract(widgetOrigin, newOffset, result);
  }

  /**
   * Converts a local rotation axis to its world space direction vector
   * @param transform - The local to world transform
   * @param localAxis - The axis in local coordinates (e.g., X, Y, or Z unit vector)
   * @param result - The Cartesian3 to store the result
   * @returns The normalized rotation axis direction in world space
   */
  static getRotationAxisInWorldSpace(
    transform: Matrix4,
    localAxis: Cartesian3,
    result: Cartesian3,
  ): Cartesian3 {
    const modelOrigin = Matrix4.getTranslation(transform, modelOriginScratch);
    let axisEndWorld = Matrix4.multiplyByPoint(
      transform,
      localAxis,
      axisEndWorldScratch,
    );
    let axisVectorWorld = Cartesian3.subtract(
      axisEndWorld,
      modelOrigin,
      axisVectorWorldScratch,
    );
    return Cartesian3.normalize(axisVectorWorld, result);
  }
}
